import React, { useState, useEffect } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { adminService, type AdminReseller, type CreateResellerPayload } from '../../services/admin.service';
import { formatDate } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';

type ModalMode = 'create' | 'edit' | null;

const EMPTY_CREATE: CreateResellerPayload = {
  resellerName: '', crNumber: '', contactEmail: '', adminName: '', adminMobile: '', adminUsername: '',
};

export const AdminResellersPage: React.FC = () => {
  const { showToast } = useToast();
  const [resellers, setResellers] = useState<AdminReseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<ModalMode>(null);
  const [editTarget, setEditTarget] = useState<AdminReseller | null>(null);
  const [createForm, setCreateForm] = useState<CreateResellerPayload>({ ...EMPTY_CREATE });
  const [editForm, setEditForm] = useState({ name: '', crNumber: '', contactEmail: '' });
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<AdminReseller | null>(null);

  const refresh = () => {
    adminService.getResellers().then(setResellers).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const openCreate = () => { setCreateForm({ ...EMPTY_CREATE }); setModal('create'); };
  const openEdit = (r: AdminReseller) => {
    setEditTarget(r);
    setEditForm({ name: r.name, crNumber: r.crNumber, contactEmail: r.contactEmail });
    setModal('edit');
  };

  const validateCreate = (): string | null => {
    if (!createForm.resellerName.trim()) return 'Reseller name is required';
    if (!/^\d{1,10}$/.test(createForm.crNumber)) return 'CR number must be 1–10 digits';
    if (!createForm.contactEmail.trim()) return 'Contact email is required';
    if (!createForm.adminName.trim()) return 'Admin name is required';
    if (!/^\+9665\d{8}$/.test(createForm.adminMobile)) return 'Mobile must be Saudi format: +9665XXXXXXXX';
    if (!createForm.adminUsername.trim()) return 'Admin username is required';
    return null;
  };

  const handleCreate = () => {
    const err = validateCreate();
    if (err) { showToast(err, 'error'); return; }
    setSaving(true);
    adminService.createReseller(createForm)
      .then((res) => {
        showToast(res.message, 'success');
        setModal(null);
        refresh();
      })
      .catch((e) => showToast(e?.response?.data?.message ?? 'Failed to create reseller', 'error'))
      .finally(() => setSaving(false));
  };

  const handleEdit = () => {
    if (!editTarget) return;
    setSaving(true);
    adminService.updateReseller(editTarget.id, editForm)
      .then(() => { showToast('Reseller updated', 'success'); setModal(null); refresh(); })
      .catch(() => showToast('Failed to update reseller', 'error'))
      .finally(() => setSaving(false));
  };

  const handleToggleActive = (r: AdminReseller, activate: boolean) => {
    const fn = activate ? adminService.activateReseller : adminService.deactivateReseller;
    fn(r.id)
      .then(() => { showToast(activate ? 'Reseller activated' : 'Reseller deactivated', 'success'); refresh(); })
      .catch(() => showToast('Action failed', 'error'));
    setConfirmDeactivate(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <AppNav title="Resellers" subtitle={`${resellers.length} reseller organizations`} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button onClick={openCreate} style={btnPrimaryStyle}>+ Add Reseller</button>
        </div>

        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e9ecef', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                  <Th>Reseller</Th>
                  <Th>CR #</Th>
                  <Th>Contact Email</Th>
                  <Th>Admin User</Th>
                  <Th>Agents</Th>
                  <Th>Active</Th>
                  <Th>Created</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {resellers.map((r) => {
                  const isActive = r.activeUserCount > 0;
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <Td><span style={{ fontWeight: 600 }}>{r.name}</span></Td>
                      <Td><span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{r.crNumber}</span></Td>
                      <Td>{r.contactEmail}</Td>
                      <Td><span style={{ fontFamily: 'monospace', fontSize: '13px', color: r.adminUsername ? '#374151' : '#9ca3af' }}>{r.adminUsername ?? '—'}</span></Td>
                      <Td>{r.activeUserCount} / {r.userCount}</Td>
                      <Td>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: isActive ? '#16a34a' : '#9ca3af', marginRight: '6px' }} />
                        {isActive ? 'Active' : 'Inactive'}
                      </Td>
                      <Td>{formatDate(r.createdAt)}</Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEdit(r)} style={btnSecondarySmall}>Edit</button>
                          {isActive ? (
                            <button onClick={() => setConfirmDeactivate(r)} style={{ ...btnSecondarySmall, color: '#dc2626', borderColor: '#fecaca' }}>Deactivate</button>
                          ) : (
                            <button onClick={() => handleToggleActive(r, true)} style={{ ...btnSecondarySmall, color: '#16a34a', borderColor: '#bbf7d0' }}>Activate</button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
                {resellers.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>No resellers yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create modal */}
      {modal === 'create' && (
        <Modal title="Add New Reseller" onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <FormField label="Reseller Name" value={createForm.resellerName} onChange={(v) => setCreateForm({ ...createForm, resellerName: v })} />
            <FormField label="CR Number" value={createForm.crNumber} onChange={(v) => setCreateForm({ ...createForm, crNumber: v })} />
            <FormField label="Contact Email" type="email" value={createForm.contactEmail} onChange={(v) => setCreateForm({ ...createForm, contactEmail: v })} span />
            <FormField label="Admin Name" value={createForm.adminName} onChange={(v) => setCreateForm({ ...createForm, adminName: v })} />
            <FormField label="Admin Mobile (+9665XXXXXXXX)" value={createForm.adminMobile} onChange={(v) => setCreateForm({ ...createForm, adminMobile: v })} />
            <FormField label="Admin Username" value={createForm.adminUsername} onChange={(v) => setCreateForm({ ...createForm, adminUsername: v })} span />
          </div>
          <p style={{ fontSize: '12px', color: '#6c757d', margin: '12px 0 0' }}>
            A Keycloak account will be created and a temporary password will be emailed to the contact email.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <button onClick={() => setModal(null)} style={btnSecondaryStyle}>Cancel</button>
            <button onClick={handleCreate} disabled={saving} style={btnPrimaryStyle}>
              {saving ? 'Creating…' : 'Create Reseller'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {modal === 'edit' && editTarget && (
        <Modal title={`Edit ${editTarget.name}`} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <FormField label="Reseller Name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} span />
            <FormField label="CR Number" value={editForm.crNumber} onChange={(v) => setEditForm({ ...editForm, crNumber: v })} />
            <FormField label="Contact Email" type="email" value={editForm.contactEmail} onChange={(v) => setEditForm({ ...editForm, contactEmail: v })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <button onClick={() => setModal(null)} style={btnSecondaryStyle}>Cancel</button>
            <button onClick={handleEdit} disabled={saving} style={btnPrimaryStyle}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Deactivate confirm */}
      {confirmDeactivate && (
        <Modal title="Deactivate Reseller" onClose={() => setConfirmDeactivate(null)}>
          <p style={{ fontSize: '14px', color: '#374151' }}>
            This will disable all Keycloak accounts for <strong>{confirmDeactivate.name}</strong> and their agents will no longer be able to log in.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <button onClick={() => setConfirmDeactivate(null)} style={btnSecondaryStyle}>Cancel</button>
            <button onClick={() => handleToggleActive(confirmDeactivate, false)} style={{ ...btnPrimaryStyle, background: '#dc2626' }}>
              Deactivate
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const FormField: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string; span?: boolean }> = ({ label, value, onChange, type = 'text', span }) => (
  <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
    <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', marginBottom: '5px' }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid #e9ecef', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
  </div>
);

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
    <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '560px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6c757d' }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{children}</th>
);
const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td style={{ padding: '12px 16px', color: '#374151' }}>{children}</td>
);

const btnPrimaryStyle: React.CSSProperties = { padding: '8px 18px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
const btnSecondaryStyle: React.CSSProperties = { padding: '7px 14px', background: '#fff', color: '#374151', border: '1px solid #e9ecef', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' };
const btnSecondarySmall: React.CSSProperties = { padding: '4px 10px', background: '#fff', color: '#374151', border: '1px solid #e9ecef', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit' };
